import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ArrowLeft, ShieldCheck, FileText, AlertCircle, Mail } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import { applyLocalizedCanonical } from "../lib/seoMeta";

const MAIL = <a href="mailto:privacy@machinepilot.eu" className="text-amber-400 underline"/>;

export default function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => { applyLocalizedCanonical("/privacy"); }, []);
  useEffect(() => { document.title = `${t("legal.privacy")} · MachinePilot`; }, [t, i18n.language]);

  const p = (k) => `legalPages.privacy.${k}`;
  const items = (k) => t(p(k), { returnObjects: true }) || [];

  return (
    <div className="theme-swiss min-h-screen bg-slate-950 text-slate-200">
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="privacy-back-home">
            <BrandIcon size={32} />
            <div className="font-display font-bold text-slate-100 group-hover:text-blue-400 transition-colors">MachinePilot</div>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1">
            <ArrowLeft size={14}/> Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="privacy-page">
        <div className="mb-8">
          <div className="label-tech text-amber-400 mb-2">{t(p("label"))}</div>
          <h1 className="font-display font-bold text-4xl text-slate-100 mb-2 flex items-center gap-3">
            <ShieldCheck size={32} className="text-amber-400"/> {t("legal.privacy")}
          </h1>
          <p className="text-sm text-slate-500">{t("legalPages.updated", { date: t("legalPages.dateValue") })} · Reg. UE 2016/679 (GDPR)</p>
        </div>

        <Section title={t(p("s1title"))}>
          <p>
            <Trans i18nKey={p("s1body")} components={{ b: <strong/>, mono: <span className="font-mono-data"/>, mail: MAIL }}/>
          </p>
        </Section>

        <Section title={t(p("s2title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s2items").map((it, i) => (
              <li key={`s2-${i}`}><strong>{it.b}</strong>{it.t}</li>
            ))}
          </ul>
        </Section>

        <Section title={t(p("s3title"))}>
          <ul className="list-disc pl-6 space-y-2">
            {items("s3items").map((it, i) => (
              <li key={`s3-${i}`}><strong>{it.b}</strong>{it.t}</li>
            ))}
          </ul>
        </Section>

        <Section title={t(p("s4title"))} highlight>
          <p className="mb-3">
            <Trans i18nKey={p("s4intro")} components={{ b: <strong/> }}/>
          </p>
          <ul className="list-disc pl-6 space-y-2">
            {items("s4items").map((it, i) => (
              <li key={`s4-${i}`}><strong>{it.b}</strong>{it.t}</li>
            ))}
          </ul>
        </Section>

        <Section title={t(p("s5title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s5items").map((it, i) => (
              <li key={`s5-${i}`}><strong>{it.b}</strong>{it.t}</li>
            ))}
          </ul>
        </Section>

        <Section title={t(p("s6title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s6items").map((it, i) => <li key={`s6-${i}`}>{it}</li>)}
          </ul>
        </Section>

        <Section title={t(p("s7title"))}>
          <p className="mb-2">{t(p("s7intro"))}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><Trans i18nKey={p("s7i1")} components={{ b: <strong/>, set: <Link to="/settings" className="text-amber-400 underline"/> }}/></li>
            <li><Trans i18nKey={p("s7i2")} components={{ b: <strong/> }}/></li>
            <li><Trans i18nKey={p("s7i3")} components={{ b: <strong/>, set: <Link to="/settings" className="text-amber-400 underline"/> }}/></li>
            <li><Trans i18nKey={p("s7i4")} components={{ b: <strong/>, mail: MAIL }}/></li>
            <li><Trans i18nKey={p("s7i5")} components={{ b: <strong/>, ext: <a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-amber-400 underline"/> }}/></li>
          </ul>
        </Section>

        <Section title={t(p("s8title"))}>
          <p>
            <Trans i18nKey={p("s8body")} components={{ set: <Link to="/settings" className="text-amber-400 underline"/>, cook: <Link to="/cookies" className="text-amber-400 underline"/> }}/>
          </p>
        </Section>

        <Section title={t(p("s9title"))}>
          <ul className="list-disc pl-6 space-y-1">
            {items("s9items").map((it, i) => <li key={`s9-${i}`}>{it}</li>)}
          </ul>
        </Section>

        <Section title={t(p("s10title"))}>
          <p>{t(p("s10body"))}</p>
        </Section>

        <div className="mt-10 p-5 rounded-md border border-amber-500/40 bg-amber-500/5 flex items-start gap-3">
          <Mail size={18} className="text-amber-400 flex-shrink-0 mt-0.5"/>
          <div className="text-sm text-slate-300">
            <strong className="text-amber-300">{t(p("contactLabel"))}</strong>{" "}
            <a href="mailto:privacy@machinepilot.eu" className="text-amber-400 underline">privacy@machinepilot.eu</a>
            {" · "}{t(p("contactSuffix"))}
          </div>
        </div>

        <div className="mt-8 flex gap-3 text-sm text-slate-400">
          <Link to="/terms" className="hover:text-amber-400 flex items-center gap-1"><FileText size={14}/> {t("legal.terms")}</Link>
          <Link to="/cookies" className="hover:text-amber-400 flex items-center gap-1"><AlertCircle size={14}/> {t("legal.cookies")}</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children, highlight }) {
  return (
    <section className={`mb-6 p-5 rounded-lg border ${highlight ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800 bg-slate-900/40"}`}>
      <h2 className="font-display font-semibold text-slate-100 mb-3 text-lg">{title}</h2>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}
